import { compose, withProps } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import { useLocation } from 'js/lib/useRouter';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import Naptime from 'bundles/naptimejs';
import PromotionEligbility from 'bundles/naptimejs/resources/promotionEligibilities.v1';
import FindByPromoCodeId from 'bundles/teams-v2/queries/FindByPromoCodeId.graphql';
import type {
  FindByEnterprisePromotionIdQuery as FindByEnterprisePromotionIdQueryType,
  FindByEnterprisePromotionIdQueryVariables,
  FindByEnterprisePromotionIdQuery_PromotionsV3Resource_byEnterprisePromotionId_elements as UnderlyingPromo,
} from 'bundles/teams-v2/queries/__generated__/FindByEnterprisePromotionIdQuery';
import type {
  FindByPromoCodeId_Dedupe1Query,
  FindByPromoCodeId_Dedupe1QueryVariables,
} from 'bundles/teams-v2/queries/__generated__/FindByPromoCodeId';
import type { FindByPromoCodeQuery_EnterprisePromotionsV1Resource_findByPromoCode_elements as FindByPromo } from 'bundles/teams-v2/queries/__generated__/FindByPromoCodeQuery';
import { findByEnterprisePromotionIdQuery } from 'bundles/teams-v2/queries/assemblerQueries';

export type PropsFromGraphQlEnterprisePromotionMap = {
  enterprisePromotion?: Pick<FindByPromo, 'id' | 'promotionId'>;
};

export type PropsFromGraphQlUnderlyingPromotionMap = {
  promotion?: Pick<UnderlyingPromo, 'id' | 'promotionName' | 'endsAt' | 'startsAt' | 'discountPercent'>;
};

export type PropsFromPromotionEligibility = {
  isPromotionEligible?: boolean;
};

export type WithRouterPromoCodeProps = {
  promoCode?: string;
};

export type WithPromoCodeToPromotionProps = WithRouterPromoCodeProps &
  PropsFromGraphQlUnderlyingPromotionMap &
  PropsFromGraphQlEnterprisePromotionMap;

function firstQueryValue(value: string | string[] | null | undefined): string | undefined {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value ?? undefined;
}

/** @deprecated Use useRouterPromoCode instead */
export const withRouterPromoCode = connectToRouter<WithRouterPromoCodeProps, {}>((router) => {
  return { promoCode: firstQueryValue(router.location.query?.promoCode) };
});

export const useRouterPromoCode = (): WithRouterPromoCodeProps => {
  const location = useLocation();
  return { promoCode: firstQueryValue(location.query?.promoCode) };
};

export const findByPromoCodeQueryData = waitForGraphQL<
  WithRouterPromoCodeProps & PropsFromGraphQlEnterprisePromotionMap,
  FindByPromoCodeId_Dedupe1Query,
  FindByPromoCodeId_Dedupe1QueryVariables,
  WithPromoCodeToPromotionProps
>(FindByPromoCodeId, {
  skip: ({ promoCode }: WithRouterPromoCodeProps) => !promoCode,
  options: ({ promoCode }: WithRouterPromoCodeProps) => ({
    variables: {
      promoCodeId: promoCode!,
    },
    errorPolicy: 'all',
    context: { clientName: 'gatewayGql' },
  }),
  props: ({ data, ownProps }) => {
    const enterprisePromotion = data?.EnterprisePromotionQueries?.getByPromoCodeId.enterprisePromotion;
    if (!enterprisePromotion) {
      return {};
    }
    return {
      promoCode: ownProps.promoCode,
      enterprisePromotion: {
        id: enterprisePromotion.id,
        promotionId: parseInt(enterprisePromotion.promotion.id),
      },
    };
  },
});

export const findByEnterprisePromotionIdData = waitForGraphQL<
  WithRouterPromoCodeProps & PropsFromGraphQlEnterprisePromotionMap & PropsFromGraphQlUnderlyingPromotionMap,
  FindByEnterprisePromotionIdQueryType,
  FindByEnterprisePromotionIdQueryVariables,
  WithPromoCodeToPromotionProps
>(findByEnterprisePromotionIdQuery, {
  skip: ({ enterprisePromotion }: PropsFromGraphQlEnterprisePromotionMap) => !enterprisePromotion,
  options: ({ enterprisePromotion }) => ({
    variables: {
      id: enterprisePromotion!.id,
    },
    errorPolicy: 'all',
  }),
  props: ({ data, ownProps }) => {
    const promotionsV3 = data?.PromotionsV3Resource?.byEnterprisePromotionId?.elements?.[0];
    if (!promotionsV3) {
      return {
        promoCode: ownProps.promoCode,
        enterprisePromotion: ownProps.enterprisePromotion,
      };
    }
    return {
      promoCode: ownProps.promoCode,
      enterprisePromotion: ownProps.enterprisePromotion,
      promotion: promotionsV3,
    };
  },
});

export const withPromoCodeToPromotion = compose<WithPromoCodeToPromotionProps, WithRouterPromoCodeProps>(
  findByPromoCodeQueryData,
  findByEnterprisePromotionIdData
);

type PromoEligibilitySlice = {
  id: string;
  eligibilityCode?: string;
};

type WithPromotionEligibilityNaptimeOuter = {
  promoCode?: string;
  promotion?: UnderlyingPromo;
};

type WithPromotionEligibilityNaptimeInner = {
  promoEligibility?: PromoEligibilitySlice;
};

type WithPromotionEligibilityWithInner = {
  isPromotionEligible: boolean;
};

type WithPromotionEligibilityWithOuter = {
  promoEligibility?: PromoEligibilitySlice;
};

export const withPromotionEligibility = compose<PropsFromPromotionEligibility, WithPromoCodeToPromotionProps>(
  Naptime.createContainer<WithPromotionEligibilityNaptimeInner, WithPromotionEligibilityNaptimeOuter>(
    ({ promoCode, promotion }) => {
      if (!promoCode || !promotion) {
        return {};
      }

      return {
        promoEligibility: PromotionEligbility.get(`${promoCode}~EnterpriseContract!~0`, {
          fields: ['id', 'eligibilityCode'],
        }),
      };
    }
  ),
  withProps<WithPromotionEligibilityWithInner, WithPromotionEligibilityWithOuter>(({ promoEligibility }) => {
    return {
      isPromotionEligible: promoEligibility?.eligibilityCode === 'ELIGIBLE',
    };
  })
);
